export const BACKGROUND_EFFECTS = ['none', 'lightrays', 'vanta-waves', 'aurora'] as const;

export type BackgroundEffect = (typeof BACKGROUND_EFFECTS)[number];

/** Legacy values remain readable so old Appwrite preferences can fall back safely. */
export type StoredBackgroundEffect = BackgroundEffect | 'vanta-dots' | 'vanta-fog';

/** The Aurora shader ramp takes exactly 3 colour stops (positions 0 / 0.5 / 1). */
export type AuroraColorStops = readonly [string, string, string];

export const DEFAULT_AURORA_COLOR_STOPS: AuroraColorStops = ['#EC4899', '#4815B4', '#7C3AED'];

const BACKGROUND_EFFECT_SET = new Set<string>(BACKGROUND_EFFECTS);

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

/** Normalize one value to a `#RRGGBB` string, or null when it isn't a usable hex colour. */
const normalizeHexColour = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return HEX_COLOR_RE.test(withHash) ? withHash.toUpperCase() : null;
};

export const isBackgroundEffect = (value: unknown): value is BackgroundEffect =>
  typeof value === 'string' && BACKGROUND_EFFECT_SET.has(value);

export const normalizeBackgroundEffect = (value: unknown): BackgroundEffect =>
  isBackgroundEffect(value) ? value : 'none';

/**
 * Parse a stored `background_aurora_colors` pref into 3 valid hex stops.
 * Accepts a comma-separated string ("#EC4899,#4815B4,#7C3AED") or an array.
 * Falls back PER-POSITION to the default so one corrupt stop never blanks
 * the whole ramp — and legacy "null" strings resolve to the full default.
 */
export const parseAuroraColors = (value: unknown): AuroraColorStops => {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim() !== '' && value.trim() !== 'null'
      ? value.split(',')
      : [];

  return [0, 1, 2].map((i) => normalizeHexColour(raw[i]) ?? DEFAULT_AURORA_COLOR_STOPS[i]) as AuroraColorStops;
};

/** Storage form for `background_aurora_colors` — comma-separated hex stops. */
export const serializeAuroraColors = (stops: AuroraColorStops): string =>
  stops.map((stop) => normalizeHexColour(stop) ?? DEFAULT_AURORA_COLOR_STOPS[stops.indexOf(stop)]).join(',');
